import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Link,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import OTPInput from "react-otp-input";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import { Constants } from "../../Utils/constants";

const OTP_LENGTH = 5;

const Verification = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const location = useLocation();

  const [otp, setOtp] = useState("");
  const [email, setEmail] = useState("");
  const [flow, setFlow] = useState("");
  const [userId, setUserId] = useState("");
  const [isError, setIsError] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setEmail(params.get("email") || "");
    setFlow(params.get("flow") || "");
    setUserId(params.get("id") || "");
  }, [location]);

  useEffect(() => {
    if (otp.length === OTP_LENGTH) {
      setIsError(false);
    }
  }, [otp]);

  const handleResendCode = async () => {
    if (!email) {
      toast.error("Missing email address");
      return;
    }
    setResending(true);
    try {
      const body = { email };
      const headers = { "Content-Type": "application/json" };
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.RESEND_CODE,
        JSON.stringify(body),
        headers
      );
      if (data?.status === true) {
        toast.success(data.message || "Code resent");
      } else {
        toast.error(data?.message || "Failed to resend code");
      }
    } catch (err) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== OTP_LENGTH) {
      setIsError(true);
      return;
    }
    setVerifying(true);
    try {
      const body = { id: Number(userId), code: otp };
      const headers = { "Content-Type": "application/json" };
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.VERIFICATIONCODE_ORGANIZATION,
        JSON.stringify(body),
        headers
      );
      if (data?.status === true) {
        toast.success(data.message || "Email verified");
        if (flow === Constants.Keys.forgetPass) {
          const query = new URLSearchParams({ uid: userId, email }).toString();
          navigate(`/verification/resetpassword?${query}`);
        } else if (flow === Constants.Keys.register) {
          const query = new URLSearchParams({ step: "1", id: userId }).toString();
          navigate(`/registerforms?${query}`);
        } else {
          navigate("/");
        }
      } else {
        toast.error(data?.message || "Invalid verification code");
      }
    } catch (err) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: isDark ? "#121212" : "#f5f7fa",
      }}
    >
      <Grid container sx={{ minHeight: "100vh" }}>
        {/* Left Half - Image */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            minHeight: { xs: "220px", md: "100vh" },
            alignItems: "center",
            justifyContent: "center",
            backgroundImage: `url(${process.env.PUBLIC_URL}/loginBG.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark
                ? "linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.4) 100%)"
                : "linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.3) 100%)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.1) 100%)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              textAlign: "center",
              px: 4,
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/white.png`}
              alt="SportsApp Logo"
              sx={{
                width: { xs: 70, md: 120 },
                height: "auto",
                mb: { xs: 2, md: 4 },
                mx: "auto",
                filter: "drop-shadow(0 8px 24px rgba(0, 0, 0, 0.3))",
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: "1.2rem", md: "1.75rem" },
                fontWeight: 700,
                color: "#ffffff",
                mb: { xs: 1, md: 2 },
                textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                letterSpacing: "-0.02em",
              }}
            >
              Welcome to SportsApp
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.8rem", md: "1.125rem" },
                color: "rgba(255, 255, 255, 0.95)",
                maxWidth: "450px",
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Manage your clubs and connect with your community
            </Typography>
          </Box>
        </Grid>

        {/* Right Half - Verification Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: { xs: 3, md: 6 },
          }}
        >
          <Container maxWidth="sm" sx={{ width: "100%" }}>
            <Box
              sx={{
                backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                borderRadius: "24px",
                padding: { xs: 3, md: 5 },
                boxShadow: isDark
                  ? "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)"
                  : "0 20px 60px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)",
                border: isDark
                  ? "1px solid rgba(255, 255, 255, 0.08)"
                  : "1px solid rgba(0, 0, 0, 0.06)",
                backdropFilter: "blur(10px)",
                maxWidth: "480px",
                mx: "auto",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 4,
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.25rem", md: "2rem" },
                    fontWeight: 700,
                    color: isDark ? "#ffffff" : "#1a202c",
                    mb: 0.5,
                    letterSpacing: "-0.01em",
                    textAlign: "center",
                  }}
                >
                  Confirm Your Email
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.8rem", md: "0.9375rem" },
                    color: isDark ? "#cbd5e1" : "#718096",
                    fontWeight: 400,
                    textAlign: "center",
                  }}
                >
                  Enter the code sent to{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, color: isDark ? "#818cf8" : "#1976d2" }}
                  >
                    {email || "your email"}
                  </Box>
                </Typography>
              </Box>

              {/* OTP Input */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={OTP_LENGTH}
                  shouldAutoFocus
                  containerStyle={{ display: "flex", gap: "12px" }}
                  renderInput={(inputProps) => (
                    <input
                      {...inputProps}
                      style={{
                        width: "48px",
                        height: "56px",
                        fontSize: "1.5rem",
                        fontWeight: 600,
                        textAlign: "center",
                        borderRadius: "8px",
                        outline: "none",
                        color: isDark ? "#ffffff" : "#1a202c",
                        backgroundColor: isDark ? "#242424" : "#f7fafc",
                        border: isError
                          ? "2px solid #ef4444"
                          : isDark
                          ? "1px solid rgba(255, 255, 255, 0.1)"
                          : "1px solid #e2e8f0",
                      }}
                    />
                  )}
                />
              </Box>

              {/* Error text */}
              {isError && (
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "#ef4444",
                    textAlign: "center",
                    mb: 1,
                  }}
                >
                  Verification code is required
                </Typography>
              )}

              {/* Resend */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 1 }}>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: isDark ? "#cbd5e1" : "#718096",
                  }}
                >
                  Didn't get a code?{" "}
                  <Link
                    component="button"
                    type="button"
                    onClick={handleResendCode}
                    disabled={resending}
                    sx={{
                      fontSize: "0.875rem",
                      color: isDark ? "#818cf8" : "#1976d2",
                      textDecoration: "none",
                      fontWeight: 500,
                      cursor: resending ? "not-allowed" : "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {resending ? "Resending..." : "Resend Code"}
                  </Link>
                </Typography>
              </Box>

              {/* Confirm Button */}
              <Button
                fullWidth
                variant="contained"
                disabled={verifying}
                onClick={handleVerify}
                sx={{
                  mt: 2,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: "8px",
                  textTransform: "none",
                  backgroundColor: isDark ? "#6366f1" : "#1976d2",
                  boxShadow: isDark
                    ? "0 4px 12px rgba(99, 102, 241, 0.3)"
                    : "0 4px 12px rgba(25, 118, 210, 0.3)",
                  "&:hover": {
                    backgroundColor: isDark ? "#4f46e5" : "#1565c0",
                    boxShadow: isDark
                      ? "0 6px 16px rgba(99, 102, 241, 0.4)"
                      : "0 6px 16px rgba(25, 118, 210, 0.4)",
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": {
                    backgroundColor: isDark ? "#475569" : "#cbd5e1",
                    color: isDark ? "#64748b" : "#94a3b8",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {verifying ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : (
                  "Confirm"
                )}
              </Button>
            </Box>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Verification;
